import { Link } from "react-router-dom";
import { ChevronRight, Calendar, Clock } from "lucide-react";

interface BlogPostHeaderProps {
  title: string;
  subtitle: string;
  category: string;
  categoryColor?: "primary" | "accent";
  date: string;
  dateFormatted: string;
  readTime: string;
  coverImage: string;
  coverImageAlt: string;
  breadcrumbLabel: string;
}

const BlogPostHeader = ({
  title,
  subtitle,
  category,
  categoryColor = "primary",
  date,
  dateFormatted,
  readTime,
  coverImage,
  coverImageAlt,
  breadcrumbLabel,
}: BlogPostHeaderProps) => {
  const categoryBgClass = categoryColor === "accent" 
    ? "bg-accent/10 text-accent" 
    : "bg-primary/10 text-primary";

  return (
    <header className="pt-24 md:pt-28">
      {/* Cover Image */}
      <div className="w-full bg-muted">
        <img
          src={coverImage}
          alt={coverImageAlt}
          className="w-full aspect-[21/9] max-h-[30vh] object-cover"
        />
      </div>

      {/* Content */}
      <div className="bg-gradient-to-b from-primary/5 to-background py-8 md:py-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav 
            className="mb-6 flex items-center gap-2 text-sm text-muted-foreground"
            aria-label="Breadcrumb"
          >
            <Link 
              to="/" 
              className="hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary rounded-sm"
            >
              Início
            </Link>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
            <Link 
              to="/blog" 
              className="hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary rounded-sm"
            >
              Blog
            </Link>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
            <span className="text-foreground" aria-current="page">{breadcrumbLabel}</span>
          </nav>

          <div className="mx-auto max-w-4xl">
            {/* Meta info */}
            <div className="mb-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className={`rounded-full px-3 py-1 font-medium ${categoryBgClass}`}>
                {category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" aria-hidden="true" />
                <time dateTime={date}>{dateFormatted}</time>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" aria-hidden="true" />
                <span>{readTime}</span>
              </span>
            </div>

            {/* Title with subtitle */}
            <h1 className="mb-6 text-3xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {title}
              <span className="block text-primary">{subtitle}</span>
            </h1>
          </div>
        </div>
      </div>
    </header>
  );
};

export default BlogPostHeader;
